import { useEventTracking } from '@/hooks/useEventTracking'
import { App, DirectiveBinding } from 'vue'

interface ExposureConfig {
    areaRate: number
    stayDuration: number
    repeated: boolean
    eventValue: Record<string, any>
}

interface ExposureContext {
    observer: IntersectionObserver
    timer: ReturnType<typeof setTimeout> | null
    hasReported: boolean
    config: ExposureConfig
}

type ExposureHTMLElement = HTMLElement & {
    _exposureContext?: ExposureContext
}

const exposureDirective = {
    mounted(el: ExposureHTMLElement, binding: DirectiveBinding) {
        initObserver(el, binding)
    },
    updated(el: ExposureHTMLElement, binding: DirectiveBinding) {
        cleanup(el)
        initObserver(el, binding)
    },
    unmounted(el: ExposureHTMLElement) {
        cleanup(el)
    }
}

/**
 * Initializes an IntersectionObserver on the given HTML element to track its exposure within the viewport.
 * 
 * @param el - The target HTML element to observe. Must implement the `ExposureHTMLElement` interface.
 * @param binding - The directive binding object containing configuration values.
 * 
 * 配置参数说明：
 * - `areaRate` (number): 元素可见面积比例阈值，默认为 0.5。
 * - `stayDuration` (number): 元素需持续可见的最短时间（毫秒），默认为 500。
 * - `repeated` (boolean): 是否允许重复上报曝光事件，默认为 true。
 * 
 * 功能说明：
 * - 当元素进入视口且可见面积达到设定阈值时，触发 `handleExposureStart`。
 * - 当元素离开视口或可见面积低于阈值时，触发 `handleExposureEnd`。
 * - 通过 `IntersectionObserver` 实现高性能曝光检测。
 * - 在元素上挂载 `_exposureContext`，用于存储观察器、定时器、上报状态及配置信息。
 */
function initObserver(el: ExposureHTMLElement, binding: DirectiveBinding) {
    const config: ExposureConfig = {
        areaRate: binding.value?.area_rate ?? 0.5,
        stayDuration: binding.value?.stay_duration ?? 500,
        repeated: binding.value?.repeated ?? true,
        eventValue: binding.value?.eventValue ?? {}
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio >= config.areaRate) {
                handleExposureStart(el, config)
            } else {
                handleExposureEnd(el)
            }
        })
    }, {
        threshold: [config.areaRate],
        rootMargin: '0px'
    })

    el._exposureContext = {
        observer,
        timer: null,
        hasReported: false,
        config
    }

    observer.observe(el)
}

function handleExposureStart(el: ExposureHTMLElement, config: ExposureConfig) {
    if (!config.repeated && el._exposureContext?.hasReported) return

    el._exposureContext!.timer = setTimeout(() => {
        reportExposure(el, config)
        if (el._exposureContext) {
            el._exposureContext.hasReported = true
        }
    }, config.stayDuration)
}

function handleExposureEnd(el: ExposureHTMLElement) {
    if (el._exposureContext?.timer) {
        clearTimeout(el._exposureContext.timer)
        el._exposureContext.timer = null
    }
}

function reportExposure(el: ExposureHTMLElement, config: ExposureConfig) {
    useEventTracking({
        event: 'hp_exposure_event',
        eventValue: config.eventValue
    })
}

function cleanup(el: ExposureHTMLElement) {
    if (el._exposureContext) {
        if (el._exposureContext.timer) {
            clearTimeout(el._exposureContext.timer)
        }
        el._exposureContext.observer.unobserve(el)
        delete el._exposureContext
    }
}

export default exposureDirective;
